import type { Scope } from '../api/types/permissions';
import type { RoleAssignmentInfoLocal } from '../api/types/MeLocal';
import type { LabelResolver } from '../hooks/useLabelResolver';
import { ORGANIZATION_GROUP_NAME, PLATFORM_SCOPE_NAME } from './labelConstants';
import { levelName, organizationName } from './scopeLabels';

/**
 * Scopes válidos por role seed del tenant. Cada role tiene UN `scope`:
 *   tenant_admin → TENANT. Los roles de niveles bajo el tenant traen su `scope` real del backend.
 *
 * Roles desconocidos / custom → todos los scopes no-PLATFORM (un sofom_admin no otorga PLATFORM).
 *
 * NOTA (TD): esto sigue siendo una matriz client-side. Lo ideal (Fase 1.B.WEB / follow-up) es
 * derivar el scope del role del backend (`SharedRoleRow.scope`, ya existe) exponiéndolo en el list
 * de roles, y eliminar esta matriz.
 */
const ROLE_SCOPES: Record<string, Scope[]> = {
    tenant_super_admin: ['TENANT'],
    tenant_admin: ['TENANT'],
};

const CUSTOM_FALLBACK: Scope[] = ['TENANT'];

// Rank de jerarquía: menor = más alto. Espeja SCOPE_RANK del backend (PLATFORM > TENANT > LEVEL_N).
// Partial: el enum generado `Scope` aún incluye scopes legacy (RETAILER/STORE/GROUP, se remueven en
// Fase 5) que el front YA NO usa → no se rankean. Un scope sin rank cae al criterio "el backend decide".
const SCOPE_RANK: Partial<Record<Scope, number>> = {
    PLATFORM: 0,
    TENANT: 1,
    // DEC-RBAC-090: niveles genéricos bajo TENANT. OJO: esta escala (rank, menor=más alto) es la del
    // FRONT y está INVERTIDA respecto a SCOPE_PRIVILEGE del backend (privilege, mayor=más amplio) — NO
    // es un espejo. Solo se compara actor-vs-rol (y ningún actor es LEVEL_N), nunca nivel-vs-nivel, así
    // que el valor relativo entre L1/L2/L3 no afecta ninguna comparación real hoy; se ordena por profundidad.
    LEVEL_1: 2,
    LEVEL_2: 3,
    LEVEL_3: 4,
};

export function allowedScopesForRole(roleId: string | null): Scope[] {
    if (!roleId) return CUSTOM_FALLBACK;
    return ROLE_SCOPES[roleId] ?? CUSTOM_FALLBACK;
}

export function isScopeValidForRole(roleId: string, scope: Scope): boolean {
    return allowedScopesForRole(roleId).includes(scope);
}

/**
 * ¿El actor (con su scope) puede ASIGNAR/CREAR un usuario con este rol? Espeja la regla del backend
 * (`ScopeValidationService._rankAllows`): el actor asigna roles de rank >= al suyo (su nivel para
 * abajo), o del MISMO nivel sólo si tiene `create.lateral` (allowOwnLevel). Los roles custom (fuera de
 * la matriz seed) se muestran — el front no puede inferir su scope; el backend re-enforza al crear.
 */
export function canActorAssignRole(
    actorScope: Scope | undefined,
    roleId: string,
    allowOwnLevel: boolean,
): boolean {
    if (!actorScope) return false;
    if (!(roleId in ROLE_SCOPES)) return true; // custom → el backend decide
    const roleScope = ROLE_SCOPES[roleId][0];
    return canActorAssignScope(actorScope, roleScope, allowOwnLevel);
}

/**
 * Variante de `canActorAssignRole` que recibe el `roleScope` REAL del rol (de `listRoles`) en vez
 * de inferirlo por `roleId` con la matriz seed. Es la forma correcta para roles CUSTOM, que no están
 * en la matriz pero sí traen su `scope` del backend. Misma regla de rank que `_rankAllows` del backend.
 *
 * `roleScope` undefined (rol sin scope en el list — backend viejo o dato faltante) → se muestra/permite
 * y el backend re-enforza al crear (igual criterio que un rol custom en `canActorAssignRole`).
 */
export function canActorAssignScope(
    actorScope: Scope | undefined,
    roleScope: Scope | undefined,
    allowOwnLevel: boolean,
): boolean {
    if (!actorScope) return false;
    if (!roleScope) return true; // scope desconocido → el backend decide
    const actorRank = SCOPE_RANK[actorScope];
    const roleRank = SCOPE_RANK[roleScope];
    // Rank desconocido (scope legacy fuera de la escala o dato faltante) → se muestra/permite y el
    // backend re-enforza al crear (mismo criterio que un roleScope undefined).
    if (actorRank === undefined || roleRank === undefined) return true;
    return allowOwnLevel ? actorRank <= roleRank : actorRank < roleRank;
}

/**
 * Scope de MAYOR autoridad entre los assignments del actor (menor `SCOPE_RANK`). Reemplaza el uso de
 * `roleAssignments[0]?.scope`, que tomaba un assignment ARBITRARIO y podía resolver a un scope débil
 * (ej. LEVEL_1) aunque el actor tuviera uno más fuerte (TENANT/PLATFORM) — filtrando de más los roles.
 *
 * OJO: usa `SCOPE_RANK` (menor = más autoridad), NO el `SCOPE_ORDER` de useAuth.ts (escala invertida).
 *
 * - Scopes sin rank (legacy GROUP/RETAILER/STORE) se consideran los más débiles.
 * - Si NINGÚN assignment tiene rank → fallback al primero (preserva la lenidad "backend decide":
 *   `canActorAssignScope` con `actorRank === undefined` retorna true).
 * - Sin assignments → undefined (`canActorAssignScope` retorna false → no ofrece roles).
 */
export function strongestActorScope(assignments: RoleAssignmentInfoLocal[]): Scope | undefined {
    if (assignments.length === 0) return undefined;
    let best: Scope | undefined;
    let bestRank = Infinity;
    for (const assignment of assignments) {
        const rank = SCOPE_RANK[assignment.scope];
        if (rank !== undefined && rank < bestRank) {
            bestRank = rank;
            best = assignment.scope;
        }
    }
    return best ?? assignments[0].scope;
}

const ROLE_GROUP_ORDER: Scope[] = ['TENANT', 'LEVEL_1', 'LEVEL_2', 'LEVEL_3', 'PLATFORM'];

function groupLabel(scope: Scope, labels?: LabelResolver, tenantId?: string): string {
    if (scope === 'PLATFORM') return PLATFORM_SCOPE_NAME;
    if (scope === 'TENANT') {
        return labels && tenantId ? organizationName(labels, tenantId) : ORGANIZATION_GROUP_NAME;
    }
    return levelName(labels, scope);
}

/**
 * Agrupa los roles por nivel para un `<optgroup>` por nivel (Organización → niveles → Plataforma, sin
 * scope conocido a «Otros»). Con `labels`, los grupos llevan los nombres del tenant.
 */
export function groupRolesByScope<T extends { scope?: Scope }>(
    roles: T[],
    labels?: LabelResolver,
    tenantId?: string,
): { key: string; label: string; roles: T[] }[] {
    const groups = ROLE_GROUP_ORDER.map((scope) => ({
        key: scope,
        label: groupLabel(scope, labels, tenantId),
        roles: roles.filter((r) => r.scope === scope),
    }));
    const known = new Set<Scope>(ROLE_GROUP_ORDER);
    const others = roles.filter((r) => !r.scope || !known.has(r.scope));
    if (others.length > 0) groups.push({ key: 'OTHER', label: 'Otros', roles: others });
    return groups.filter((g) => g.roles.length > 0);
}
